import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useInvitations } from '@/composables/useInvitations';
import { useRouteContext } from '../useRouteContext';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { IGenericUser } from '@/interfaces';
import { IFilter } from '@chax-at/prisma-filter-common';
import { useQueryClient } from '@tanstack/vue-query';
import { IUserFilter } from 'common/interface/filter.interface';
import { useDisplayName } from '@/composables/useDisplayName';
import { computed, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWhoAmI } from '../useWhoAmI';

export type TUserOrInvitation = {
  roleType: Components.Schemas.UserRoleType;
  user: IGenericUser;
  userRole?: Components.Schemas.UserRoleWithUserDto;
  invitation?: Components.Schemas.TenantUserInvitationDto;
};

interface IUsersComposable {
  refresh: () => Promise<void>;
  removeUserRole: (userRole: Components.Schemas.UserRoleWithUserDto) => Promise<void>;
  updateUserRole: (
    userRoleId: number,
    data: Components.Schemas.UpdateUserRoleDto,
    showToast?: boolean,
  ) => Promise<void>;
  users: ComputedRef<Components.Schemas.UserRoleWithUserDto[]>;
  usersAndInvitations: ComputedRef<TUserOrInvitation[]>;
  amIOnlyActiveUserInTenant: ComputedRef<boolean>;
}

export function useUsers(): IUsersComposable {
  const { allInvitations } = useInvitations();
  const { editSuccessToast, catchError, deleteSuccessToast } = useToast();
  const { confirm } = useConfirmDialog();
  const { t } = useI18n();
  const { isSchoolContext } = useRouteContext();
  const { whoAmI, refresh: refreshWhoAmI } = useWhoAmI();
  const { nameSortation, getUserDisplayName } = useDisplayName();

  const getUsers = async (): Promise<Components.Schemas.UserRoleWithUserDto[]> => {
    // the backend orders by the user's nameSortation setting
    const filter: IFilter<IUserFilter> = {
      filter: [],
    };
    const result = await Api.Routes.Tenant.retrieveUserRoles({ $filter: filter });
    return result.rows;
  };

  const usersListQueryKey = computed(() => ['users', 'list', nameSortation.value]);
  const queryClient = useQueryClient();
  const query = useQueryWithToast({
    queryKey: usersListQueryKey,
    queryFn: getUsers,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: usersListQueryKey });
  };

  const allUsers = computed<Components.Schemas.UserRoleWithUserDto[]>(() => {
    return query.data.value ?? [];
  });

  const updateUserRole = async (
    userRoleId: number,
    data: Components.Schemas.UpdateUserRoleDto,
    showToast = true,
  ): Promise<void> => {
    await catchError(async () => {
      await Api.Routes.Tenant.updateUserRole({ userRoleId }, data);
      await Promise.all([refresh(), refreshWhoAmI()]);
      if (showToast) editSuccessToast();
    });
  };

  const removeUserRole = async (userRole: Components.Schemas.UserRoleWithUserDto): Promise<void> => {
    const confirmed = await confirm({
      title: t('teacher.remove'),
      description: t('teacher.removeConfirmText', {
        userName: getUserDisplayName(userRole.user),
      }),
      confirmButtonLabel: t('general.delete'),
      declineButtonLabel: t('general.cancel'),
      theme: 'danger',
    });

    if (!confirmed) return;

    await catchError(async () => {
      await Api.Routes.Tenant.removeUserRole({ userRoleId: userRole.id });
      await Promise.all([refresh(), refreshWhoAmI()]);
      deleteSuccessToast();
    });
  };

  const usersAndInvitations = computed<TUserOrInvitation[]>(() => {
    const result: TUserOrInvitation[] = [];

    for (const userRole of allUsers.value) {
      result.push({
        user: userRole.user,
        userRole,
        roleType: userRole.roleType,
      });
    }

    if (isSchoolContext.value) {
      for (const invitation of allInvitations.value) {
        result.push({
          user: {
            firstName: '',
            lastName: '',
            email: invitation.email,
            isInvitation: true,
          },
          roleType: invitation.userRoleType,
          invitation,
        });
      }
    }

    return result;
  });

  const amIOnlyActiveUserInTenant = computed(() => {
    return usersAndInvitations.value.length === 1 && usersAndInvitations.value[0].user.id === whoAmI.value?.id;
  });

  return {
    refresh,
    removeUserRole,
    updateUserRole,
    users: allUsers,
    usersAndInvitations,
    amIOnlyActiveUserInTenant,
  };
}
